import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "classes", "error", "info", "isFullWidth", "isReadOnly", "onBlur", "onChange", "onClick", "onFocus", "onSubmit", "onEsc", "overrides", "placeholder", "toolbar", "toolbarStyle", "value", "mention", "customActions", "compactMode", "autofocus", "canSubmit", "onChangeFocus", "emoji", "hideClear", "submitTooltip", "isBasic", "basicType", "loading", "getEditorRef", "isSubmitDisabled", "withCustomToolbar", "chatbox", "linkConfig"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, createContext, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Mention from '@tiptap/extension-mention';
import HardBreak from '@tiptap/extension-hard-break';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Link from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import Emoji from './components/emojiExtension.js';
import Placeholder from './components/placeholderExtension';
import FontSize from './components/fontSizeExtension';
import Icon from '../../general/Icon';
import InputWrapper from '../components/InputWrapper';
import Menu from './components/Menu';
import { getEmojiConfig, getSuggestionsConfig } from './components/extensionsConfig';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../utils/styles';
import styles from './styles';
var FREQUENTLY_USED_ITEMS_TO_SHOW = 36;
var useStyles = createUseStyles(styles, 'RichText');
export var RichTextContext = /*#__PURE__*/createContext();
var RichText = /*#__PURE__*/memo(function (_ref) {
  var classNameProp = _ref.className,
    classesProp = _ref.classes,
    error = _ref.error,
    info = _ref.info,
    isFullWidth = _ref.isFullWidth,
    isReadOnly = _ref.isReadOnly,
    _onBlur = _ref.onBlur,
    onChange = _ref.onChange,
    onClick = _ref.onClick,
    _onFocus = _ref.onFocus,
    onSubmit = _ref.onSubmit,
    onEsc = _ref.onEsc,
    overridesProp = _ref.overrides,
    _placeholder = _ref.placeholder,
    toolbar = _ref.toolbar,
    toolbarStyle = _ref.toolbarStyle,
    value = _ref.value,
    mention = _ref.mention,
    customActions = _ref.customActions,
    compactMode = _ref.compactMode,
    autofocus = _ref.autofocus,
    canSubmit = _ref.canSubmit,
    onChangeFocus = _ref.onChangeFocus,
    emoji = _ref.emoji,
    hideClear = _ref.hideClear,
    submitTooltip = _ref.submitTooltip,
    isBasic = _ref.isBasic,
    basicType = _ref.basicType,
    loading = _ref.loading,
    getEditorRef = _ref.getEditorRef,
    isSubmitDisabled = _ref.isSubmitDisabled,
    withCustomToolbar = _ref.withCustomToolbar,
    chatbox = _ref.chatbox,
    linkConfig = _ref.linkConfig,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var classes = useClasses(useStyles, classesProp);
  var override = getOverrides(overridesProp, RichText.overrides);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    showingMenuPopover = _useState4[0],
    setShowingMenuPopover = _useState4[1];
  var _useState5 = useState({}),
    _useState6 = _slicedToArray(_useState5, 2),
    editorContent = _useState6[0],
    setEditorContent = _useState6[1];
  var showingMention = useRef(false);
  var showingEmoji = useRef(false);
  var editorDivRef = useRef(null);
  var getExtensions = useMemo(function () {
    var extensions = [Placeholder.configure({
      placeholder: function placeholder() {
        return _placeholder || null;
      }
    }), StarterKit.configure({
      hardBreak: !chatbox
    }), Underline, TextStyle, FontFamily, FontSize, Color, Link.configure(linkConfig)];
    if (chatbox) {
      extensions.push(HardBreak.extend({
        addKeyboardShortcuts: function addKeyboardShortcuts() {
          var _this = this;
          var handleShiftEnter = function handleShiftEnter() {
            if (_this.editor.isActive('bulletList') || _this.editor.isActive('orderedList')) {
              _this.editor.commands.first(function (_ref2) {
                var commands = _ref2.commands;
                return [function () {
                  return commands.splitListItem('listItem');
                }];
              });
            } else {
              _this.editor.commands.first(function (_ref3) {
                var commands = _ref3.commands;
                return [function () {
                  return commands.newlineInCode();
                }, function () {
                  return commands.createParagraphNear();
                }, function () {
                  return commands.liftEmptyBlock();
                }, function () {
                  return commands.splitBlock();
                }];
              });
            }
          };
          return {
            Enter: function Enter() {
              return true;
            },
            // Prevent extra line-break on submiting via Enter
            'Shift-Enter': handleShiftEnter
          };
        }
      }));
    }
    if (emoji) {
      extensions.push(Emoji.configure({
        enableEmoticons: true,
        suggestion: getEmojiConfig({
          emoji: emoji,
          showingEmoji: showingEmoji
        })
      }));
    }
    if (mention && mention.fetchSuggestions && typeof mention.fetchSuggestions === 'function') {
      extensions.push(Mention.configure({
        suggestion: getSuggestionsConfig({
          mention: mention,
          showingMention: showingMention
        }),
        renderLabel: function renderLabel(_ref4) {
          var node = _ref4.node;
          return "".concat(node.attrs.label);
        }
      }));
    }
    return extensions;
  }, [chatbox, emoji, mention, _placeholder]);
  var editor = useEditor({
    editable: !isReadOnly,
    extensions: getExtensions,
    content: value,
    onCreate: function onCreate(_ref5) {
      var editor = _ref5.editor;
      var content = {
        text: editor.getText(),
        html: editor.getHTML(),
        json: editor.getJSON()
      };
      setEditorContent(content);
      if (content.text.length && autofocus) {
        setFocused(true);
        editor.commands.focus('end');
      }
    },
    onUpdate: function onUpdate(_ref6) {
      var editor = _ref6.editor;
      var content = {
        text: editor.getText(),
        html: editor.getHTML(),
        json: editor.getJSON()
      };
      setEditorContent(content);
      onChange && onChange(content);
    },
    onFocus: function onFocus(_ref7) {
      var event = _ref7.event;
      setFocused(true);
      _onFocus && _onFocus(event);
      onChangeFocus && onChangeFocus(true);
    },
    onBlur: function onBlur(_ref8) {
      var event = _ref8.event;
      if (compactMode) return;
      setFocused(false);
      _onBlur && _onBlur(event);
      onChangeFocus && onChangeFocus(false);
    }
  });
  useEffect(function () {
    if (editor && getEditorRef) getEditorRef(editor);
  }, [editor, getEditorRef]);
  useEffect(function () {
    if (autofocus && editor && !showingMenuPopover) {
      editor.commands.focus('end');
      setFocused(true);
    }
  }, [autofocus, editor, showingMenuPopover]);
  useEffect(function () {
    var handleClickOutside = function handleClickOutside(event) {
      if (!(editorDivRef !== null && editorDivRef !== void 0 && editorDivRef.current.contains(event.target)) && !showingMenuPopover) {
        setFocused(false);
        _onBlur && _onBlur(event);
        onChangeFocus && onChangeFocus(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return function () {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [_onBlur, onChangeFocus, showingMenuPopover]);
  var handleEmojiCache = useCallback(function (name) {
    if (!(emoji !== null && emoji !== void 0 && emoji.cache) && !(emoji !== null && emoji !== void 0 && emoji.saveCache)) return;
    var newCache = _objectSpread({}, emoji.cache());
    newCache[name] = newCache[name] ? newCache[name] + 1 : 1;
    newCache = Object.fromEntries(Object.entries(newCache).sort(function (a, b) {
      return b[1] - a[1];
    }).slice(0, FREQUENTLY_USED_ITEMS_TO_SHOW));
    emoji.saveCache(newCache);
  }, [emoji]);
  var handleClear = useCallback(function (e) {
    e.stopPropagation();
    editor.commands.clearContent(true);
    editor.commands.focus();
  }, [editor]);
  var handleClick = useCallback(function (e) {
    if (compactMode) e.stopPropagation();
    if (!focused && !isReadOnly && !showingMenuPopover) {
      editor.commands.focus();
      setFocused(true);
    }
    onClick && onClick(e);
  }, [editor, focused, isReadOnly, onClick, compactMode, showingMenuPopover]);
  var handleSubmit = useCallback(function () {
    if (!onSubmit) {
      // Standard enter behavior: new paragraph
      editor.commands.splitBlock();
    } else {
      onSubmit();
      editor.commands.blur();
      editor.commands.clearContent(true);
    }
  }, [editor, onSubmit]);
  var handleKeyDown = useCallback(function (e) {
    if (e.key === 'Escape' && !showingMention.current && !showingEmoji.current && !showingMenuPopover) {
      editor.commands.blur();
      setFocused(false);
      onEsc && onEsc(e);
    }
    if (e.key === 'Escape' && (showingMention.current || showingEmoji.current)) {
      showingMention.current = false;
      showingEmoji.current = false;
    }
    if (e.key === 'Tab' && (showingEmoji.current || showingMenuPopover)) {
      showingEmoji.current = false;
      return;
    }
    if (chatbox && e.key === 'Enter' && !e.shiftKey) {
      if (showingMention.current || showingEmoji.current || showingMenuPopover) {
        showingMention.current = false;
        showingEmoji.current = false;
        return;
      }
      if (loading || isSubmitDisabled || canSubmit && !canSubmit(editorContent)) return;
      handleSubmit();
    }
  }, [editor, onEsc, isSubmitDisabled, canSubmit, editorContent, handleSubmit, showingMenuPopover, loading, chatbox]);
  var getIcons = useMemo(function () {
    switch (true) {
      case (editorContent === null || editorContent === void 0 ? void 0 : editorContent.text) && !isReadOnly && !compactMode && !isBasic && !hideClear:
        return /*#__PURE__*/React.createElement(Icon, {
          name: "closeSmall",
          size: "large",
          className: classes.actionIcon,
          onClick: handleClear
        });
      case isReadOnly:
        return /*#__PURE__*/React.createElement(Icon, {
          name: "lockOutline",
          size: "large",
          className: classes.actionIcon,
          color: theme.colors.neutral600
        });
      default:
        return null;
    }
  }, [classes.actionIcon, editorContent === null || editorContent === void 0 ? void 0 : editorContent.text, handleClear, isReadOnly, theme.colors.neutral600, compactMode, hideClear, isBasic]);
  var inputWrapperProps = useMemo(function () {
    var _editorContent$text, _classNames;
    return _objectSpread(_objectSpread({
      className: classNames(classes.root, (_classNames = {}, _defineProperty(_classNames, classes.focused, focused), _defineProperty(_classNames, classes.error, error), _defineProperty(_classNames, classes.isFullWidth, isFullWidth), _defineProperty(_classNames, classes.isReadOnly, isReadOnly), _defineProperty(_classNames, classes.hasFixedToolbar, toolbarStyle === 'fixed'), _defineProperty(_classNames, classes.compactMode, !isBasic && compactMode && !focused), _defineProperty(_classNames, classes.compactModeFocused, !isBasic && compactMode && (focused || !!(editorContent !== null && editorContent !== void 0 && (_editorContent$text = editorContent.text) !== null && _editorContent$text !== void 0 && _editorContent$text.length))), _defineProperty(_classNames, classes.isBasic, isBasic), _defineProperty(_classNames, classes.basicStatic, basicType === 'static'), _classNames), classNameProp),
      error: error,
      info: info
    }, otherProps), override.root);
  }, [classNameProp, classes, compactMode, editorContent, error, focused, info, isFullWidth, isReadOnly, otherProps, toolbarStyle, isBasic, basicType, override]);
  var editorWrapperProps = useMemo(function () {
    return _objectSpread({
      className: classes.editorWrapper,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      ref: editorDivRef
    }, override.editorWrapper);
  }, [classes, handleClick, override, handleKeyDown]);
  var editorProps = useMemo(function () {
    return _objectSpread({
      editor: editor,
      className: classes.editor
    }, override.editor);
  }, [classes, editor, override]);
  var menuProps = useMemo(function () {
    return {
      compactMode: compactMode,
      customActions: customActions,
      editorContent: editorContent,
      focused: focused,
      handleSubmit: handleSubmit,
      toolbar: toolbar,
      toolbarStyle: toolbarStyle,
      submitTooltip: submitTooltip,
      isBasic: isBasic,
      loading: loading,
      isSubmitDisabled: isSubmitDisabled
    };
  }, [compactMode, customActions, editorContent, focused, handleSubmit, toolbar, toolbarStyle, submitTooltip, isBasic, loading, isSubmitDisabled]);
  return /*#__PURE__*/React.createElement(RichTextContext.Provider, {
    value: {
      editor: editor,
      emoji: emoji,
      mention: mention,
      cache: (emoji === null || emoji === void 0 ? void 0 : emoji.cache()) || {},
      saveCache: handleEmojiCache,
      setShowingMenuPopover: setShowingMenuPopover
    }
  }, /*#__PURE__*/React.createElement(InputWrapper, inputWrapperProps, /*#__PURE__*/React.createElement("div", editorWrapperProps, /*#__PURE__*/React.createElement(EditorContent, editorProps), !withCustomToolbar && /*#__PURE__*/React.createElement(Menu, menuProps), getIcons)));
});
RichText.overrides = ['root', 'editorWrapper', 'editor'];
RichText.defaultProps = _objectSpread(_objectSpread({}, InputWrapper.defaultProps), {}, {
  isFullWidth: false,
  isReadOnly: false,
  onBlur: function onBlur() {},
  onChange: function onChange() {},
  onClick: function onClick() {},
  onFocus: function onFocus() {},
  toolbar: [{
    hint: 'Bold',
    item: 'b'
  }, {
    hint: 'Italic',
    item: 'i'
  }, {
    hint: 'Underline',
    item: 'u'
  }, {
    hint: 'Strike',
    item: 'del'
  }, {
    hint: 'Unordered List',
    item: 'ul'
  }, {
    hint: 'Ordered List',
    item: 'ol'
  }],
  toolbarStyle: 'floating',
  value: '',
  compactMode: false,
  autofocus: false,
  hideClear: false,
  submitTooltip: 'Send ⏎',
  basicType: 'dynamic',
  loading: false,
  isSubmitDisabled: false,
  withCustomToolbar: false,
  chatbox: false,
  linkConfig: {
    openOnClick: true,
    autolink: true
  }
});
process.env.NODE_ENV !== "production" ? RichText.propTypes = {
  className: PropTypes.string,
  /** Error will be displayed below the component with style changes */
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** Info will be displayed below the component */
  info: PropTypes.string,
  isFullWidth: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onSubmit: PropTypes.func,
  onEsc: PropTypes.func,
  placeholder: PropTypes.string,
  toolbar: PropTypes.arrayOf(PropTypes.shape({
    hint: PropTypes.string,
    item: PropTypes.oneOf(['b', 'i', 'u', 'ol', 'ul', 'del'])
  })),
  toolbarStyle: PropTypes.oneOf(['floating', 'fixed']),
  /** An HTML string */
  value: PropTypes.string,
  mention: PropTypes.shape({
    /** Fetch suggestions for the mentions feature */
    fetchSuggestions: PropTypes.func,
    /** Tooltip text for the mentions button in the toolbar */
    tooltip: PropTypes.string,
    texts: PropTypes.shape({
      /** Text advice to show at the top of the mentions popover */
      advice: PropTypes.string,
      /** Text to show if the mentions search can't find a match */
      noResults: PropTypes.string
    }),
    /** Max visible items in the suggestions list */
    maxVisibleItems: PropTypes.number
  }),
  /** Enable compact mode: 'Single line height' editor with action buttons on the right that expands on focus */
  compactMode: PropTypes.bool,
  /** Enable autofocus */
  autofocus: PropTypes.bool,
  canSubmit: PropTypes.func,
  onChangeFocus: PropTypes.func,
  emoji: PropTypes.shape({
    /** Tooltip text for the mentions button in the toolbar */
    tooltip: PropTypes.string,
    /** Max visible items in the suggestions list */
    maxVisibleItems: PropTypes.number
  }),
  /** Hide clear action button in the editor's input area */
  hideClear: PropTypes.bool,
  submitTooltip: PropTypes.string,
  /** No toolbar is shown, no emoji tool, just the text box and Submit button on the right */
  isBasic: PropTypes.bool,
  basicType: PropTypes.PropTypes.oneOf(['static', 'dynamic']),
  /** In basic richtext a loader can be shown instead of submit button */
  loading: PropTypes.bool,
  /** Allows direct access to the editor and set content */
  getEditorRef: PropTypes.func,
  /** Disables the submit button */
  isSubmitDisabled: PropTypes.bool,
  /** Set it to `true` if you're planning to provide a custom toolbar for the editor */
  withCustomToolbar: PropTypes.bool,
  /** Set it to `true` if you want the editor to behave like in a chat client. p.e 'Enter' submits the message */
  chatbox: PropTypes.bool,
  /** Set it to override link extension's config */
  linkConfig: PropTypes.shape({
    openOnClick: PropTypes.bool,
    autolink: PropTypes.bool
  })
} : void 0;
export default RichText;